/**
 * Amplitude — 웹 이벤트 트래킹.
 * @description 앱(`src/utils/amplitudes/trackings.ts`)과 **같은 이벤트 이름·같은 속성 키**를
 *   쓴다. 이름이 갈리면 한 퍼널을 두 개로 봐야 한다.
 *
 *   앱은 기본 속성에 `platform: Platform.OS` 를 실어 "ios"/"android" 를 보낸다. 웹은 같은
 *   자리에 `"web"` 을 넣는다 — 같은 대시보드에서 한 축으로 웹/앱을 가를 수 있다.
 *
 *   `app_version` 은 보내지 않는다. 앱 스토어 버전을 가리키는 축이라 랜딩의 package.json
 *   버전을 흘려 넣으면 그 차원이 오염된다. 웹 여부는 `platform` 으로 이미 갈린다.
 *
 *   SDK 는 **동적으로 불러온다**. 정적 import 하면 가입 화면 초기 번들이 70KB 가까이
 *   늘어난다. 로드 전에 발생한 이벤트는 큐에 담았다가 준비되면 순서대로 흘려보낸다 —
 *   퍼널의 첫 이벤트(`SignUp Started`)가 바로 이 구간에서 나기 때문에 버리면 안 된다.
 */
import { gaEvent } from "./ga";

type AmplitudeModule = typeof import("@amplitude/analytics-browser");

const API_KEY = process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY;

/** 웹임을 나타내는 값 — 앱의 `Platform.OS`("ios"/"android")와 같은 속성에 들어간다. */
const PLATFORM = "web";

interface QueuedEvent {
  name: string;
  properties: Record<string, unknown>;
}

let sdk: AmplitudeModule | null = null;
let loading: Promise<void> | null = null;
let pendingUserId: string | null = null;
const queue: QueuedEvent[] = [];

function flush(): void {
  if (!sdk) return;
  if (pendingUserId !== null) {
    sdk.setUserId(pendingUserId);
    pendingUserId = null;
  }
  while (queue.length > 0) {
    const event = queue.shift();
    if (event) sdk.track(event.name, event.properties);
  }
}

export function initAmplitude(): void {
  // 키가 없으면 조용히 넘어간다 — 트래킹 미설정이 화면을 막을 이유는 없다.
  if (!API_KEY || loading || typeof window === "undefined") return;

  loading = import("@amplitude/analytics-browser")
    .then(async (module) => {
      await module.init(API_KEY, {
        // 이벤트 이름은 우리가 직접 정하므로 자동 수집은 페이지뷰·세션만 켠다.
        autocapture: {
          pageViews: true,
          sessions: true,
          formInteractions: false,
          fileDownloads: false,
        },
      }).promise;
      sdk = module;
      flush();
    })
    .catch(() => {
      // 로드 실패 시 큐를 비워 메모리에 이벤트가 쌓이지 않게 한다.
      queue.length = 0;
    });
}

function baseProps(): Record<string, unknown> {
  return {
    route: typeof window === "undefined" ? undefined : window.location.pathname,
    platform: PLATFORM,
    app_language:
      typeof navigator === "undefined" ? undefined : navigator.language,
  };
}

/**
 * 이벤트 하나를 앰플리튜드와 GA **양쪽에** 보낸다.
 * @description 속성은 한 번만 만들어 둘이 나눠 쓴다. 따로 만들면 한쪽에만 필드가 붙는다.
 *
 *   GA 는 앰플리튜드 키가 없어도 보낸다 — 서로 다른 도구이고, 한쪽 설정이 빠졌다고 다른
 *   쪽까지 조용히 멈추면 대시보드가 비어 있는 이유를 찾는 데 하루가 간다.
 */
export function trackEvent(
  eventName: string,
  eventProperties?: Record<string, unknown>,
): void {
  if (typeof window === "undefined") return;
  const properties = { ...baseProps(), ...eventProperties };

  gaEvent(eventName, properties);

  if (!API_KEY) return;
  if (sdk) {
    sdk.track(eventName, properties);
    return;
  }
  queue.push({ name: eventName, properties });
}

/** 로그인 직후 서버가 준 userId 를 붙인다 — 그 전 이벤트는 익명 기기 id 로 쌓인다. */
export function identifyUser(userId: number | string): void {
  if (!API_KEY || typeof window === "undefined") return;
  if (sdk) {
    sdk.setUserId(String(userId));
    return;
  }
  pendingUserId = String(userId);
}
